import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CardContent, Stack, Box, Typography, TextField, Alert, Tabs, Tab, CircularProgress } from '@mui/material';
import { LocalHospitalOutlined, EditOutlined } from '@mui/icons-material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { EncounterType } from '../../../services/EncounterService/types/encounterTypes';
import { useGetEncounterQuery } from '../../../services/EncounterService';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import { setEncounterAction as setEncounter, clearEncounterAction as clearEncounter } from '../../../services/EncounterService/slice';
import { PageContainer, PageCard, SectionHeader, SectionPaper } from '../../common/components';
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import PatientInfoView from '../components/views/PatientInfoView';
import { AdmitDateEdit, DischargeDateEdit, AdmissionTypeEdit, AdmissionSourceEdit, PhysicianSelectEdit } from '../components/fieldEditors';
import { UpdateEncounterButton, CancelEncounterButton } from '../components/commands';
import { ServicesGrid, InsurancesGrid, ScheduledProcedureCodesGrid } from '../components/grids';
import { ServiceAddDialog, InsuranceAddDialog, ScheduledProcedureCodeAddDialog } from '../components/dialogs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Helper component for tab panels
const CustomTabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`encounter-tabpanel-${index}`}
    aria-labelledby={`encounter-tab-${index}`}
  >
    {value === index && (
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const EncounterEditContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Get encounter ID from URL parameters
  const encounterId = searchParams.get('id') || '';
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Success/Error states
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Get encounter from state
  const encounter = useAppSelector(state => state.encounter.currentEncounter);

  // Fetch encounter data using RTK Query
  const { data: encounterData, error: encounterError, isLoading: encounterLoading } = useGetEncounterQuery(
    { id: encounterId },
    {
      skip: !encounterId,
    }
  );

  // Load encounter into state when data is fetched
  useEffect(() => {
    if (encounterData) {
      dispatch(setEncounter(encounterData));
    }
  }, [encounterData, dispatch]);

  // Clear encounter state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearEncounter());
    };
  }, [dispatch]);

  // Redirect if no encounter ID
  useEffect(() => {
    if (!encounterId) {
      navigate('/encounter/create');
      return;
    }
  }, [encounterId, navigate]);

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Success handler
  const handleUpdateSuccess = () => {
    setSuccess(true);
    setErrorMsg('');
    setTimeout(() => {
      if (encounter?.patient?.mrn && encounter?.caseIdentification?.company && encounter?.caseIdentification?.centerId) {
        navigate(`/Registration?company=${encounter.caseIdentification.company}&center=${encounter.caseIdentification.centerId}&mrn=${encounter.patient.mrn}`);
      }
    }, 2000);
  };

  // Error handler
  const handleUpdateError = (error: string) => {
    setErrorMsg(error);
    setSuccess(false);
  };

  // Loading state
  if (encounterLoading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              p: 4
            }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              Loading encounter...
            </Box>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  // Error state
  if (encounterError) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <CardContent sx={{ p: 3 }}>
              <Alert severity="error">
                Error loading encounter. Please try again or contact support.
              </Alert>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  // No encounter loaded state
  if (!encounter) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <CardContent sx={{ p: 3 }}>
              <Alert severity="warning">
                Encounter not found or failed to load.
              </Alert>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              position: 'relative',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h5" component="h1" fontWeight={600} color="#1e293b" sx={{ mb: 0.5 }}>
                  <EditOutlined sx={{ mr: 1, fontSize: 'inherit' }} />
                  Edit Encounter
                </Typography>
                <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.9rem' }}>
                  {encounter.patient?.mrn ? `Editing encounter for MRN: ${encounter.patient.mrn}` : 'Update encounter information'}
                  {encounter.caseIdentification?.visitNumber && ` | Visit #: ${encounter.caseIdentification.visitNumber}`}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <CancelEncounterButton 
                  onSuccess={handleUpdateSuccess}
                  onError={handleUpdateError}
                />
                <UpdateEncounterButton 
                  onSuccess={handleUpdateSuccess}
                  onError={handleUpdateError}
                />
                <GoBackButton />
              </Box>
            </Box>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Stack spacing={2.5}>
              {/* Patient Information Section */}
              <PatientInfoView />

              {/* Encounter Information Section */}
              <Box>
                <SectionHeader 
                  icon={<LocalHospitalOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} 
                  title="Encounter Information" 
                />
                <SectionPaper>
                  <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                    <AdmitDateEdit />
                    <DischargeDateEdit />
                    <AdmissionSourceEdit />
                    <AdmissionTypeEdit />
                  </Stack>
                  
                  <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                    <PhysicianSelectEdit />
                    <Box sx={{ flex: 1 }}></Box>
                  </Stack>
                </SectionPaper>
              </Box>

              {/* Tab Strip Section */}
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="encounter tabs">
                    <Tab label="Services" />
                    <Tab label="Insurance" />
                    <Tab label="Procedures" />
                    <Tab label="Guarantor" />
                    <Tab label="Notes" />
                  </Tabs>
                </Box>
                
                <CustomTabPanel value={tabValue} index={0}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
                      <ServiceAddDialog />
                    </Box>
                    <ServicesGrid />
                  </Stack>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={1}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
                      <InsuranceAddDialog />
                    </Box>
                    <InsurancesGrid />
                  </Stack>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={2}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="flex-end" sx={{ mb: 0.5 }}>
                      <ScheduledProcedureCodeAddDialog />
                    </Box>
                    <ScheduledProcedureCodesGrid />
                  </Stack>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={3}>
                  <SectionPaper>
                    <Typography variant="body2" color="#64748b">
                      Guarantor section - Coming soon
                    </Typography>
                  </SectionPaper>
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={4}>
                  <SectionPaper>
                    <TextField
                      label="Notes"
                      multiline
                      rows={4}
                      fullWidth
                      size="small"
                      placeholder="Enter encounter notes..."
                      value={encounter?.visitNotes?.[0]?.note || ''}
                      onChange={(e) => {
                        // Handle notes change - you may want to dispatch an action here
                        // dispatch(editEncounter({ visitNotes: [{ note: e.target.value }] }));
                      }}
                    />
                  </SectionPaper>
                </CustomTabPanel>
              </Box>

              {/* Status Messages */}
              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Encounter updated successfully! Redirecting to patient registration...
                </Alert>
              )}
              {(errorMsg) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errorMsg}
                </Alert>
              )}
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

const EncounterEdit: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <EncounterEditContent />
    </AuthenticatedTemplate>
  );
};

export default EncounterEdit;
